// App shell - top nav bar + three-column layout, recreating the
// design/project/scripts/app.jsx prototype shell.
//
// Topbar: brand (gradient orbit mark + "AI Builder Hub"), nav tabs
// (Hub / Projects / Education), centered search ("Search the hub..."),
// Feed/Editorial toggle (Hub only), theme toggle, avatar.
//
// Hub tab renders the center feed (<HubFeed/>) plus a sticky right rail with
// three live-wired panels: UPCOMING (events), LATEST PROJECTS, TOP
// CONTRIBUTORS. Projects and Education tabs reuse the existing directory
// pages (not restyled this pass).
import "@/features/hub-feed/hub-feed.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { SignInPage, UserMenu } from "@/features/auth";
import { AdminMembersPage } from "@/features/admin/AdminMembersPage";
import { CopPage } from "@/features/directories/CopPage";
import { EducationPage } from "@/features/directories/EducationPage";
import { EventsCalendar } from "@/features/directories/EventsCalendar";
import { ProjectsPage } from "@/features/directories/ProjectsPage";
import {
  BrandMark,
  Icon,
} from "@/features/hub-feed/design-atoms";
import {
  ContributorsRail,
  EventsRail,
  ProjectsRail,
} from "@/features/hub-feed/components/RailPanels";
import { GamerZonePage, GamerZoneUnlockTransition } from "@/features/gamer-zone/GamerZonePage";
import { HubFeed } from "@/features/hub-feed";
import { ThemeToggle, useTheme } from "@/features/hub-feed/ThemeToggle";
import { useProfile } from "@/lib/useProfile";
import { PostDetail } from "@/features/posting-engagement";
import { TrainingDetail } from "@/features/directories/TrainingDetail";
import { advanceSequence, isEditableTarget, type SequenceState } from "./konami";

type Page = "home" | "projects" | "education" | "cop" | "admin";
type ShellRoute = "hub" | "gamer-zone";

const NAV: { id: Page; label: string; icon: keyof typeof Icon }[] = [
  { id: "home", label: "Feed", icon: "feed" },
  { id: "projects", label: "Projects", icon: "project" },
  { id: "education", label: "Education", icon: "book" },
  { id: "cop", label: "CoP", icon: "chat" },
];

const ADMIN_NAV: { id: Page; label: string; icon: keyof typeof Icon } = {
  id: "admin",
  label: "Admin",
  icon: "shield",
};

const LAUNCH_ROUTE_SWAP_MS = 3200;
const LAUNCH_OVERLAY_CLEAR_MS = 4700;

function routeFromLocation(): ShellRoute {
  return window.location.pathname === "/gamer-zone" ? "gamer-zone" : "hub";
}

export function App() {
  const { profile, isAdmin, loading: profileLoading, setProfile } = useProfile();
  const { theme, toggle } = useTheme();
  const [route, setRoute] = useState<ShellRoute>(routeFromLocation);
  const [unlocking, setUnlocking] = useState(false);
  const [page, setPage] = useState<Page>("home");
  const [query, setQuery] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [postId, setPostId] = useState<string | null>(null);
  const [trainingId, setTrainingId] = useState<string | null>(null);
  const routeSwapTimerRef = useRef<ReturnType<typeof window.setTimeout> | undefined>(undefined);
  const overlayClearTimerRef = useRef<ReturnType<typeof window.setTimeout> | undefined>(undefined);
  const themeRef = useRef(theme);

  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  const go = useCallback((next: Page) => {
    setPage(next);
    setPostId(null);
    setTrainingId(null);
    setQuery("");
    window.scrollTo(0, 0);
  }, []);

  const goHomeFromGamerZone = useCallback(() => {
    window.history.pushState({}, "", "/");
    setRoute("hub");
    setPage("home");
    setPostId(null);
    setTrainingId(null);
    setQuery("");
    window.scrollTo(0, 0);
  }, []);

  const goGamerZone = useCallback(() => {
    window.history.pushState({}, "", "/gamer-zone");
    setRoute("gamer-zone");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onPop = () => setRoute(routeFromLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    // Gamer Zone is gated behind sign-in same as the rest of the app — an
    // unauthenticated visitor sees only SignInPage, so the Konami listener
    // must not be live for them (it would otherwise let a signed-out visitor
    // unlock a route that renders authenticated-only content).
    if (route !== "hub" || unlocking || !profile) return;

    let state: SequenceState = { index: 0, unlocked: false };
    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;

      state = advanceSequence(event.key, state);
      if (!state.unlocked) return;

      event.preventDefault();
      setUnlocking(true);
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      // Stored in refs (not effect-local vars) so this effect re-running when
      // `unlocking` flips true does not cancel the in-flight launch timers;
      // they are cleared only on unmount (see the cleanup effect below).
      routeSwapTimerRef.current = window.setTimeout(
        () => {
          if (themeRef.current !== "dark") {
            toggle();
          }
          goGamerZone();
        },
        reducedMotion ? 0 : LAUNCH_ROUTE_SWAP_MS,
      );
      overlayClearTimerRef.current = window.setTimeout(
        () => {
          setUnlocking(false);
        },
        reducedMotion ? 160 : LAUNCH_OVERLAY_CLEAR_MS,
      );
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goGamerZone, route, theme, toggle, unlocking, profile]);

  // Clear any pending unlock timers on unmount so a stray callback can't
  // navigate or toggle the theme after the app has torn down.
  useEffect(
    () => () => {
      window.clearTimeout(routeSwapTimerRef.current);
      window.clearTimeout(overlayClearTimerRef.current);
    },
    [],
  );

  // Open a post's detail view (clicking a feed row).
  const openPost = useCallback((id: string) => {
    setPostId(id);
    window.scrollTo(0, 0);
  }, []);

  // Open a Learning resource's full detail view (clicking a card's Details trigger).
  const openTraining = useCallback((id: string) => {
    setTrainingId(id);
    window.scrollTo(0, 0);
  }, []);

  // Close the calendar overlay on Escape.
  useEffect(() => {
    if (!calendarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCalendarOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [calendarOpen]);

  // Auth gate: the whole app (Hub + Gamer Zone) requires a signed-in
  // session. Nothing below this point renders until useProfile() resolves.
  if (profileLoading) {
    return (
      <div className="auth-app-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }
  if (!profile) {
    return <SignInPage />;
  }

  // `unlocking && <GamerZoneUnlockTransition />` is rendered exactly once,
  // at a stable position in this Fragment, regardless of `route`. It must
  // NOT live inside the route-conditional branches below: if it did, the
  // route flip from "hub" to "gamer-zone" mid-animation would swap out the
  // whole subtree at that position, unmounting and remounting the overlay
  // and restarting its CSS keyframe animations from 0% (the rocket
  // "re-launching" for a moment right after the Gamer Zone page loads).
  return (
    <>
      {route === "gamer-zone" ? (
        <GamerZonePage onBack={goHomeFromGamerZone} />
      ) : (
        <>
          {/* ===== TOP BAR ===== */}
          <header className="topbar">
            <div className="topbar-inner">
              <div
                className="brand"
                role="button"
                tabIndex={0}
                onClick={() => go("home")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    go("home");
                  }
                }}
              >
                <BrandMark size={42} />
                <div className="brand-word">
                  <b>AI Builder Hub</b>
                  <span>Nelnet Business Services</span>
                </div>
              </div>

              <nav className="nav" aria-label="Primary">
                {(isAdmin ? [...NAV, ADMIN_NAV] : NAV).map((n) => {
                  const Ic = Icon[n.icon];
                  const active = page === n.id;
                  return (
                    <a
                      key={n.id}
                      className={"nav-link" + (active ? " active" : "")}
                      role="button"
                      tabIndex={0}
                      aria-current={active ? "page" : undefined}
                      onClick={() => go(n.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          go(n.id);
                        }
                      }}
                    >
                      <Ic size={16} /> {n.label}
                    </a>
                  );
                })}
              </nav>

              <div className="topbar-right">
                <div className="searchbox" role="search">
                  <Icon.search size={16} />
                  <input
                    placeholder="Search the hub..."
                    value={page === "home" ? query : ""}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      if (page !== "home") setPage("home");
                    }}
                    aria-label="Search the hub"
                  />
                </div>
                <ThemeToggle theme={theme} onToggle={toggle} />
                <UserMenu
                  profile={profile}
                  onAvatarSaved={(avatar) => setProfile({ ...profile, avatar })}
                />
              </div>
            </div>
          </header>

          {/* ===== BODY ===== */}
          {/* The rail (Events/Projects/Contributors) is persistent across Hub
              pages except Admin and CoP, which own their own layout. */}
          <main className="page">
            {page === "admin" ? (
              isAdmin && <AdminMembersPage />
            ) : page === "cop" && !postId && !trainingId ? (
              <CopPage />
            ) : (
              <div className="layout">
                <div>
                  {postId ? (
                    <PostDetail postId={postId} onBack={() => setPostId(null)} />
                  ) : trainingId ? (
                    <TrainingDetail resourceId={trainingId} onBack={() => setTrainingId(null)} />
                  ) : page === "projects" ? (
                    <ProjectsPage onSelectProject={openPost} />
                  ) : page === "education" ? (
                    <EducationPage onOpenResource={openTraining} />
                  ) : (
                    <HubFeed query={query} onOpenPost={openPost} onOpenTraining={openTraining} />
                  )}
                </div>
                <aside className="rail">
                  <EventsRail onOpenCalendar={() => setCalendarOpen(true)} />
                  <ProjectsRail onSeeAll={() => go("projects")} onOpen={openPost} />
                  <ContributorsRail />
                </aside>
              </div>
            )}
          </main>

          {/* ===== CALENDAR OVERLAY ===== */}
          {calendarOpen && (
            <div className="hub-modal-overlay" onClick={() => setCalendarOpen(false)}>
              <div
                className="hub-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Events calendar"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="hub-modal__head">
                  <h2 className="hub-modal__title">Events</h2>
                  <button
                    type="button"
                    className="hub-modal__close"
                    aria-label="Close"
                    onClick={() => setCalendarOpen(false)}
                  >
                    <Icon.x size={18} />
                  </button>
                </div>
                <EventsCalendar />
              </div>
            </div>
          )}
        </>
      )}
      {unlocking && <GamerZoneUnlockTransition />}
    </>
  );
}
